import i18next from 'i18next';

import { CustomerAddressDto } from 'types/api/customer.type';
import { OrderHeaderResponse, OrderItem } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';

import {
  formatCityProvinceCodeCountry,
  formatCustomerAddressLines,
  formatShippingMethod,
} from 'helpers/formatAddressDisplay';
import { getResolvedPriceByType } from 'helpers/pricing';

import { parsePrice } from './parsePrice';

type CustomerInfo = {
  code?: string;
  companyName?: string;
  address?: string;
  postCode?: string;
  city?: string;
  province?: string;
  country?: string;
  bankName?: string;
  contactPerson?: string;
  email?: string;
  /** Province code (`LC`); the city line falls back to `province` (name) without it. */
  provinceCode?: string;
  /** `code - description`, as on the order page; falls back to `bankName`. */
  paymentCondition?: string;
};

type BuildOrderPdfPayloadParams = {
  header: OrderHeaderResponse;
  items: OrderItem[];
  customerInfo?: CustomerInfo;
  shippingAddress?: CustomerAddressDto | null;
  goodsDestination?: CustomerAddressDto | null;
  /** Selected price type. Undefined means "No Price" — omit prices from PDF. */
  priceType?: PriceType;
};

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(i18next.language === 'it' ? 'it-IT' : 'en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

const formatPrice = (price: string | null, currency: string): string => {
  if (!price) return '';
  const num = parsePrice(price);
  if (num === 0 && price !== '0') return '';
  return formatCurrency(num, currency);
};

// Address and customer blocks carry the same values as the order page (company name,
// `zip - city - province code - COUNTRY`, `code - description` payment condition, order buyer).
const buildAddressBlock = (address: CustomerAddressDto | null | undefined) => {
  if (!address) return undefined;
  return {
    code: address.code || '',
    name: address.displayName || '',
    address: formatCustomerAddressLines(address),
    cityStateCountry: formatCityProvinceCodeCountry(address),
  };
};

/**
 * Builds the PDF payload matching the Lambda's expected shape.
 *
 * Products are grouped by style (productCode + mainFabricCode),
 * with individual colour variants nested inside.
 */
export const buildOrderPdfPayload = ({
  header,
  items,
  customerInfo,
  shippingAddress,
  goodsDestination,
  priceType,
}: BuildOrderPdfPayloadParams) => {
  const currency = items[0]?.currency || 'EUR';
  const createdAt = header.createdAt ? new Date(header.createdAt) : null;
  const creationDate = createdAt
    ? createdAt.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';
  const creationTime = createdAt
    ? createdAt.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    : '';

  const totalQuantity =
    header.totalQuantity ?? items.reduce((acc, item) => acc + (item.totalQuantity ?? 0), 0);

  // Compute order total based on selected price type
  let totalPrice: number;
  let resolvedCurrency = currency;

  if (!priceType) {
    // "No Price" — no totals shown
    totalPrice = 0;
  } else {
    const resolved = header.resolvedTotals?.[priceType];
    totalPrice = resolved?.price ?? 0;
    if (resolved?.currency) {
      resolvedCurrency = resolved.currency;
    }
  }

  // Group items by style (productCode + mainFabricCode)
  const styleMap = new Map<
    string,
    { items: OrderItem[]; productCode: string; fabricCode: string }
  >();

  items.forEach((item) => {
    const key = `${item.productCode}_${item.mainFabricCode}`;
    if (!styleMap.has(key)) {
      styleMap.set(key, {
        items: [],
        productCode: item.productCode,
        fabricCode: item.mainFabricCode,
      });
    }
    styleMap.get(key)!.items.push(item);
  });

  const lang = i18next.language as 'en' | 'it';

  const products = Array.from(styleMap.values()).map((style) => {
    const firstItem = style.items[0];
    const styleTotalQty = style.items.reduce(
      (acc, item) => acc + (item.totalQuantity ?? 0),
      0
    );

    // Style total cost — same logic as StyleRow.computeColourTotal
    let styleTotalPrice = 0;
    if (priceType) {
      for (const item of style.items) {
        const useBackendResolved =
          item.resolvedTotalProductPrice != null && priceType === 'WHOLESALE';
        if (useBackendResolved) {
          styleTotalPrice += Number(item.resolvedTotalProductPrice);
        } else {
          for (const size of item.sizes ?? []) {
            if (size.quantity <= 0) continue; // eslint-disable-line no-continue
            const resolved = getResolvedPriceByType(size.resolvedPrices, priceType);
            if (resolved?.status === 'FOUND' && resolved.price) {
              styleTotalPrice += size.quantity * parsePrice(resolved.price.price);
            }
          }
        }
      }
    }

    // Determine prices from resolvedPrices when a priceType is selected
    let wholesalePrice = '';
    let suggRetailPrice = '';

    if (priceType) {
      // Find unit price from resolvedPrices for the selected type
      for (const item of style.items) {
        for (const size of item.sizes) {
          const resolved = getResolvedPriceByType(size.resolvedPrices, priceType);
          if (resolved?.status === 'FOUND' && resolved.price) {
            wholesalePrice = formatPrice(resolved.price.price, resolved.price.currency);
            break;
          }
        }
        if (wholesalePrice) break;
      }

      // If selected type is not RETAIL, also try to get retail for "suggested"
      if (priceType !== 'RETAIL') {
        for (const item of style.items) {
          for (const size of item.sizes) {
            const retail = size.resolvedPrices?.find((rp) => rp.type === 'RETAIL');
            if (retail?.status === 'FOUND' && retail.price) {
              suggRetailPrice = formatPrice(retail.price.price, retail.price.currency);
              break;
            }
          }
          if (suggRetailPrice) break;
        }
      }
    }

    const variants = style.items.map((item) => {
      const colorName =
        item.colors?.find((c) => c.colorCode === item.productColorCode)?.[
          lang === 'it' ? 'itName' : 'enName'
        ] || item.productColorCode;

      // Per-colour total — same logic as ColourRow
      let variantTotalCost = 0;
      if (priceType) {
        const useBackendResolved =
          item.resolvedTotalProductPrice != null && priceType === 'WHOLESALE';
        if (useBackendResolved) {
          variantTotalCost = Number(item.resolvedTotalProductPrice);
        } else {
          for (const size of item.sizes ?? []) {
            if (size.quantity <= 0) continue; // eslint-disable-line no-continue
            const resolved = getResolvedPriceByType(size.resolvedPrices, priceType);
            if (resolved?.status === 'FOUND' && resolved.price) {
              variantTotalCost += size.quantity * parsePrice(resolved.price.price);
            }
          }
        }
      }

      const variantCurrency = item.sizes?.find((s) => {
        const rp = getResolvedPriceByType(s.resolvedPrices, priceType);
        return rp?.status === 'FOUND' && rp.price;
      })
        ? getResolvedPriceByType(
            item.sizes.find((s) => {
              const rp = getResolvedPriceByType(s.resolvedPrices, priceType);
              return rp?.status === 'FOUND' && rp.price;
            })!.resolvedPrices,
            priceType
          )!.price!.currency
        : item.currency || currency;

      return {
        colorName,
        sizes: item.sizes.map((s) => s.size),
        quantities: item.sizes.map((s) => (s.quantity > 0 ? s.quantity : '')),
        totalQuantity: item.totalQuantity,
        totalCost: priceType ? formatCurrency(variantTotalCost, variantCurrency) : '',
      };
    });

    return {
      imageUrl: firstItem.imageUrl || '',
      productCode: style.productCode,
      fabricCode: style.fabricCode,
      colorCode: firstItem.productColorCode || '',
      sizeChart: firstItem.sizeScaleCode || '',
      description: firstItem.productName?.[lang] || firstItem.productName?.en || '',
      deliveryCode: header.deliveryCode || '',
      suggRetailPrice,
      wholesalePrice,
      totalStyleQuantity: styleTotalQty,
      totalStyleCost: priceType ? formatCurrency(styleTotalPrice, resolvedCurrency) : '',
      variants,
    };
  });

  // Build customer block
  const customerBlock = {
    code: customerInfo?.code || header.customerCode || '',
    name: customerInfo?.companyName || '',
    address: customerInfo?.address || '',
    cityStateCountry: formatCityProvinceCodeCountry({
      zipCode: customerInfo?.postCode,
      city: customerInfo?.city,
      subdivisionCode: customerInfo?.provinceCode || customerInfo?.province,
      countryName: customerInfo?.country,
    }),
    paymentCondition: customerInfo?.paymentCondition || customerInfo?.bankName || '',
    buyerNameContact: header.buyerName || '',
    emailAddress: header.buyerEmail || '',
  };
  const shippingAddressBlock = buildAddressBlock(shippingAddress);

  const payload: Record<string, unknown> = {
    order: {
      orderNumber: String(header.orderNumber ?? ''),
      creationDate,
      creationTime,
      totalAmount: priceType ? formatCurrency(totalPrice, resolvedCurrency) : '',
      totalQuantity,
      currency,
      zone: header.zone || '',
      season: header.season || '',
      collectionCode: header.collectionCode || '',
      deliveryCode: header.deliveryCode || '',
      customer: customerBlock,
      shippingAddress: shippingAddressBlock && {
        ...shippingAddressBlock,
        // The incoterm of the shipping address record itself (variant B).
        shippingMethod: formatShippingMethod(
          shippingAddress?.shippingMethod,
          i18next.language ?? ''
        ),
      },
      goodsDestination: buildAddressBlock(goodsDestination),
      orderTakenOn: header.orderTakenOn || '',
      orderNotes: header.note || '',
      products,
      totalOrderQuantity: totalQuantity,
      totalOrderValue: priceType ? formatCurrency(totalPrice, resolvedCurrency) : '',
    },
  };

  return payload;
};
